import React, { useEffect, useState, useMemo } from 'react';
import { Question } from '@/modules/Product/components/Question';
import { Container, Body2, TrashIcon, Text } from '@/ui';
import styled from 'styled-components';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { sortWithNulls } from '@/utils/helpers';
import { Button } from '@/components';
import { PlusOutlined } from '@ant-design/icons';

const createLinkFromText = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, index) =>
    urlRegex.test(part) ? (
      <a href={part} key={index} target='_blank' rel='noopener noreferrer'>
        {part}
      </a>
    ) : (
      <span key={index}>{part}</span>
    )
  );
};
export const ProductGroup = ({ productGroup, watch, preFilledQuestions, control, ...props }) => {
  const [visible, setVisible] = useState(true);
  const watchedFields = useWatch({
    control,
    name: productGroup?.visibleConditions
      ? productGroup?.visibleConditions?.map((item) => item.id)
      : []
  });

  //isVisible logic
  useEffect(() => {
    const conditions = productGroup.visibleConditions;
    const operator = productGroup.visibleOperator;

    if (conditions && Array.from(conditions).length > 0) {
      let isVisible = visible;

      if (operator === 'AND') {
        isVisible = true;
        for (const [index, value] of watchedFields.entries()) {
          if (
            Array.isArray(value)
              ? !value.includes(conditions[index].value)
              : conditions[index].value !== value
          ) {
            isVisible = false;
            break;
          }
        }
      }

      if (operator === 'OR') {
        isVisible = false;
        for (const [index, value] of watchedFields.entries()) {
          if (
            Array.isArray(value)
              ? value.includes(conditions[index].value)
              : conditions[index].value === value
          ) {
            isVisible = true;
            break;
          }
        }
      }

      if (operator === 'NOT') {
        isVisible = true;
        for (const [index, value] of watchedFields.entries()) {
          if (
            Array.isArray(value)
              ? value.includes(conditions[index].value)
              : conditions[index].value === value
          ) {
            isVisible = false;
            break;
          }
        }
      }

      if (visible !== isVisible) setVisible(isVisible);
    }
  }, [watchedFields, productGroup]);

  if (visible) {
    if (!productGroup.isMultiple) {
      return (
        <ProductGroupWrapper>
          {productGroup.title && (
            <Container $marginBottom={20}>
              <Text $fontSize={17} $fontWeight={600}>
                {createLinkFromText(productGroup.title)}
              </Text>
            </Container>
          )}

          {productGroup.productQuestions &&
            productGroup.productQuestions
              .sort((a, b) => sortWithNulls(Number(a.sortOrder), Number(b.sortOrder)))
              .map((questionObj, index) => (
                <Question
                  question={questionObj}
                  watch={watch}
                  groupVisibleConditions={productGroup.visibleConditions}
                  preFilledQuestions={preFilledQuestions}
                  control={control}
                  {...props}
                  key={index}
                />
              ))}
        </ProductGroupWrapper>
      );
    } else {
      return (
        <Multiple
          productGroup={productGroup}
          preFilledQuestions={preFilledQuestions}
          watch={watch}
          control={control}
          {...props}
        />
      );
    }
  }

  return null;
};

const Multiple = ({ productGroup, watch, control, preFilledQuestions, ...props }) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control: control,
    name: `group_${productGroup.id}`
  });

  const appendValues = useMemo(() => {
    const result = {};

    productGroup.productQuestions.forEach((item) => (result[item.id] = ''));

    return result;
  }, [productGroup]);

  useEffect(() => {
    const multipleMin = productGroup.multipleMin || 1;

    if (append && fields.length < multipleMin) {
      append(new Array(multipleMin).fill({ ...appendValues }));
    }
  }, [productGroup, appendValues, append, fields]);

  const multipleMin = useMemo(() => {
    return productGroup?.multipleMin || 1;
  }, [productGroup]);

  return (
    <div>
      <ProductGroupWrapper $paddingY={24}>
        {productGroup.title && <Body2>{createLinkFromText(productGroup.title)}</Body2>}

        {fields.map((field, fieldIndex) => {
          const groupPreFilledObj = preFilledQuestions[`group_${productGroup.id}.${fieldIndex}`];

          return (
            <MultipleBox key={field.id} $lastBox={fields.length - 1 === fieldIndex}>
              {productGroup.productQuestions &&
                productGroup.productQuestions.map((questionObj, index) => (
                  <Container $paddingY={8}>
                    <Question
                      question={questionObj}
                      watch={watch}
                      control={control}
                      groupVisibleConditions={productGroup.visibleConditions}
                      name={`group_${productGroup.id}.${fieldIndex}.${questionObj.id}`}
                      key={index}
                      preFilledQuestions={preFilledQuestions}
                      disableQuestion={groupPreFilledObj && groupPreFilledObj[questionObj.id]}
                      {...props}
                    />
                  </Container>
                ))}

              <DeleteButtonContainer>
                <TrashIcon
                  onClick={() => remove(fieldIndex)}
                  disabled={groupPreFilledObj || fieldIndex < multipleMin}
                />
              </DeleteButtonContainer>
            </MultipleBox>
          );
        })}

        {!props.disabled && (
          <Button
            text={t('Add')}
            onClick={() => append({ ...appendValues })}
            icon={<PlusOutlined />}
            disabled={productGroup.multipleMax && fields.length >= productGroup.multipleMax}
          />
        )}
      </ProductGroupWrapper>
    </div>
  );
};

const MultipleBox = styled.div`
  position: relative;
  border-bottom: 1px solid #dde4e9;
  ${(p) => (p.$lastBox ? 'border: none;' : 'padding-bottom: 16px;')}

  &:last-child {
    border-bottom: none;
  }
`;
const ProductGroupWrapper = styled(Container)`
  border-bottom: 1px solid #dde4e9;

  &:last-child {
    padding-bottom: 0;
    border-bottom: unset;
  }

  > *:not(:last-child) {
    margin-bottom: 14px;
  }
`;
const DeleteButtonContainer = styled.div`
  position: absolute;
  top: -10px;
  right: 0;
  z-index: 2;
`;
